import {
  AlertTriangle,
  ArrowRight,
  ArrowUp,
  BarChart3,
  CheckCircle,
  Clock,
  Info,
  List,
  RotateCcw,
  Shield,
  Target,
  Users,
  Workflow,
  XCircle
} from 'lucide-react';
import React, { useState } from 'react';

interface ExceptionAnalysisPopupProps {
  isOpen: boolean;
  onClose: () => void;
  stepName: string;
  stepId: string;
  exceptionData: any;
  processMapId: number;
}

const ExceptionAnalysisPopup: React.FC<ExceptionAnalysisPopupProps> = ({
  isOpen,
  onClose,
  stepName,
  stepId,
  exceptionData,
  processMapId
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetails, setShowDetails] = useState(false);

  if (!isOpen) return null;

  const { stepData, fullAnalysis } = exceptionData;
  const potentialExceptions = stepData?.potential_exceptions || [];
  const escalationMatrix = fullAnalysis?.escalation_hierarchy?.escalation_matrix || {};
  const recoveryPaths = fullAnalysis?.recovery_paths?.recovery_procedures?.find((proc: any) =>
    proc.step_name.toLowerCase().includes(stepName.toLowerCase().slice(0, 20))
  );
  const processClassification = fullAnalysis?.process_classification;
  const statistics = fullAnalysis?.statistics || {};


 const renderOverviewTab = () => {
  const stepExceptions = stepData?.potential_exceptions || [];
  const exceptionPaths = stepData?.exception_paths || [];
  const standardPath = stepData?.standard_path || {};

  return (
    <div className="space-y-6">
      {/* Exception Process Flow - USING REAL DATA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-4 flex items-center">
          <ArrowRight className="w-5 h-5 mr-2" />
          Exception Process Flow
        </h4>
        
        {/* Dynamic Process Flow Diagram */}
        <div
          className="overflow-x-auto custom-scrollbar"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#93C5FD #F3F4F6'
          }}
        >
          <div className="flex items-center justify-center space-x-6 min-w-max px-4 py-2">
            {/* Standard Process Box - REAL DATA */}
            <div className="bg-green-100 border border-green-300 rounded-lg px-4 py-3 text-center min-w-[220px] max-w-[280px]">
              <div className="font-medium text-green-800 mb-2">Standard Process</div>
              <div className="text-sm text-green-600 leading-tight break-words">
                {standardPath.description || stepName}
              </div>
            </div>

            <div className="flex items-center justify-center min-w-[24px]">
              <ArrowRight className="text-gray-400 w-6 h-6" />
            </div>

            {/* Exception Trigger Box - REAL DATA */}
            <div className="bg-red-100 border border-red-300 rounded-lg px-4 py-3 text-center min-w-[220px] max-w-[280px]">
              <div className="font-medium text-red-800 mb-2">Exception Trigger</div>
              <div className="text-sm text-red-600 leading-tight break-words">
                {stepExceptions.length > 0 ?
                  stepExceptions[0].description || 'Condition Detected' :
                  'No Exceptions Identified'
                }
              </div>
            </div>

            <div className="flex items-center justify-center min-w-[24px]">
              <ArrowRight className="text-gray-400 w-6 h-6" />
            </div>

            {/* Exception Process Box - REAL DATA */}
            <div className="bg-orange-100 border border-orange-300 rounded-lg px-4 py-3 text-center min-w-[220px] max-w-[280px]">
              <div className="font-medium text-orange-800 mb-2">Exception Process</div>
              <div className="text-sm text-orange-600 leading-tight break-words">
                {exceptionPaths.length > 0 ?
                  exceptionPaths[0].alternative_process || `${exceptionPaths.length} alternative paths` :
                  'Standard exception handling'
                }
              </div>
            </div>

            <div className="flex items-center justify-center min-w-[24px]">
              <ArrowRight className="text-gray-400 w-6 h-6" />
            </div>

            {/* Return/Escalate Box - REAL DATA */}
            <div className="bg-blue-100 border border-blue-300 rounded-lg px-4 py-3 text-center min-w-[220px] max-w-[280px]">
              <div className="font-medium text-blue-800 mb-2">Return/Escalate</div>
              <div className="text-sm text-blue-600 leading-tight break-words">
                {exceptionPaths.length > 0 ?
                  exceptionPaths[0].return_point || 'Resume or Escalate' :
                  'Resume standard process'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Exception Paths Details - REAL DATA */}
        {exceptionPaths.length > 0 && (
          <div className="mt-6 bg-white rounded-lg p-4 border">
            <h5 className="font-medium text-gray-900 mb-3">Exception Path Details</h5>
            <div className="space-y-3">
              {exceptionPaths.slice(0, 3).map((path: any, index: number) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900">{path.trigger}</div>
                    <div className="text-sm text-gray-600 mt-1">{path.alternative_process}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      <span className="font-medium">Bypass Reason:</span> {path.bypass_reason}
                    </div>
                  </div>
                </div>
              ))}
              {exceptionPaths.length > 3 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {exceptionPaths.length - 3} more exception paths
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Standard Path vs Exception Analysis - REAL DATA */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <h5 className="font-medium text-gray-900 mb-3 flex items-center">
          <Target className="w-4 h-4 mr-2 text-blue-600" />
          Process Path Analysis for: {stepName}
        </h5>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Standard Path */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h6 className="font-medium text-green-800 mb-2 flex items-center">
              <CheckCircle className="w-4 h-4 mr-2" />
              Standard Path (Normal Flow)
            </h6>
            <p className="text-sm text-green-700 mb-3">
              {standardPath.description || `Normal execution of ${stepName}`}
            </p>
            <div className="space-y-2">
              <div>
                <span className="text-xs font-medium text-green-700">Standard Controls:</span>
                <ul className="text-xs text-green-600 mt-1 space-y-1">
                  {(standardPath.controls || ['Process documentation', 'Quality checks']).map((control: string, idx: number) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-1 h-1 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                      {control}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <span className="text-xs font-medium text-green-700">Required Approvals:</span>
                <ul className="text-xs text-green-600 mt-1 space-y-1">
                  {(standardPath.approvals || ['Process completion verification']).map((approval: string, idx: number) => (
                    <li key={idx} className="flex items-center">
                      <div className="w-1 h-1 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                      {approval}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Exception Paths */}
          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <h6 className="font-medium text-orange-800 mb-2 flex items-center">
              <XCircle className="w-4 h-4 mr-2" />
              Exception Paths ({exceptionPaths.length} identified)
            </h6>
            {exceptionPaths.length > 0 ? (
              <div className="space-y-2">
                {exceptionPaths.slice(0, 2).map((path: any, index: number) => (
                  <div key={index} className="bg-white rounded p-3 border border-orange-200">
                    <div className="font-medium text-orange-900 text-sm">Path {index + 1}: {path.trigger}</div>
                    <div className="text-xs text-orange-700 mt-1">{path.alternative_process}</div>
                    <div className="text-xs text-orange-600 mt-2">
                      <span className="font-medium">Return Point:</span> {path.return_point}
                    </div>
                  </div>
                ))}
                {exceptionPaths.length > 2 && (
                  <div className="text-xs text-orange-600 text-center">
                    +{exceptionPaths.length - 2} more exception paths
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-orange-700">No exception paths identified for this step.</p>
            )}
          </div>
        </div>
      </div>

      {/* Exception Statistics - REAL DATA */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <h5 className="font-medium text-gray-900 mb-3 flex items-center">
          <Target className="w-4 h-4 mr-2 text-blue-600" />
          Exception Analysis Summary
        </h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center bg-orange-50 rounded-lg p-3 border border-orange-200">
            <div className="text-2xl font-bold text-orange-800">{stepExceptions.length}</div>
            <div className="text-sm text-orange-600">Exception Types</div>
          </div>
          <div className="text-center bg-blue-50 rounded-lg p-3 border border-blue-200">
            <div className="text-2xl font-bold text-blue-800">{exceptionPaths.length}</div>
            <div className="text-sm text-blue-600">Alternative Paths</div>
          </div>
          <div className="text-center bg-red-50 rounded-lg p-3 border border-red-200">
            <div className="text-2xl font-bold text-red-800">
              {stepExceptions.filter((exc: any) => exc.likelihood === 'high').length}
            </div>
            <div className="text-sm text-red-600">High Risk</div>
          </div>
          <div className="text-center bg-green-50 rounded-lg p-3 border border-green-200">
            <div className="text-2xl font-bold text-green-800">
              {stepExceptions.filter((exc: any) => exc.business_impact === 'low').length}
            </div>
            <div className="text-sm text-green-600">Low Impact</div>
          </div>
        </div>
      </div>

      {/* Risk Assessment Matrix - REAL DATA */}
      {stepExceptions.length > 0 && (
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <h5 className="font-medium text-gray-900 mb-3 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-2 text-yellow-600" />
            Risk Assessment Matrix
          </h5>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {stepExceptions.map((exception: any, index: number) => (
              <div key={index} className={`rounded-lg p-3 border ${
                exception.likelihood === 'high' && exception.business_impact === 'high' 
                  ? 'bg-red-50 border-red-200'
                  : exception.likelihood === 'medium' || exception.business_impact === 'medium'
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-green-50 border-green-200'
              }`}>
                <h6 className="font-medium capitalize mb-1">
                  {exception.exception_type?.replace('_', ' ') || 'Unknown Exception'}
                </h6>
                <div className="flex justify-between text-xs mb-2">
                  <span className={`px-2 py-1 rounded ${
                    exception.likelihood === 'high' ? 'bg-red-100 text-red-800' :
                    exception.likelihood === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {exception.likelihood || 'unknown'} likelihood
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    exception.business_impact === 'high' ? 'bg-red-100 text-red-800' :
                    exception.business_impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {exception.business_impact || 'unknown'} impact
                  </span>
                </div>
                <p className="text-xs text-gray-600">{exception.description || 'No description available'}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

  const renderExceptionsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Potential Exceptions Identified</h4>
        <span className="text-sm text-gray-500">{potentialExceptions.length} exceptions found</span>
      </div>

      {potentialExceptions.map((exception: any, index: number) => (
        <div key={index} className="bg-white rounded-lg border border-orange-200 overflow-hidden">
          <div className="p-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="font-medium text-orange-900 capitalize">
                {exception.exception_type?.replace('_', ' ')}
              </h5>
              <div className="flex space-x-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${exception.likelihood === 'high' ? 'bg-red-100 text-red-800' :
                  exception.likelihood === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                  {exception.likelihood} probability
                </span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${exception.business_impact === 'critical' ? 'bg-red-100 text-red-800' :
                  exception.business_impact === 'high' ? 'bg-orange-100 text-orange-800' :
                    exception.business_impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                  }`}>
                  {exception.business_impact} impact
                </span>
              </div>
            </div>

            <p className="text-sm text-gray-700 mb-3">{exception.exception_description}</p>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="font-medium text-gray-700">Detection Method:</span>
                <div className="text-gray-600 mt-1">{exception.detection_method}</div>
              </div>
              {exception.prevention_measures && (
                <div>
                  <span className="font-medium text-gray-700">Prevention:</span>
                  <div className="text-gray-600 mt-1">{exception.prevention_measures}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {potentialExceptions.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <XCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No exceptions identified for this step</p>
        </div>
      )}
    </div>
  );

  const renderRecoveryTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Recovery Procedures</h4>
        <span className="text-sm text-gray-500">
          {recoveryPaths?.exception_recoveries?.length || 0} recovery paths available
        </span>
      </div>

      {recoveryPaths?.exception_recoveries?.map((recovery: any, index: number) => (
        <div key={index} className="bg-green-50 rounded-lg p-4 border border-green-200">
          <h5 className="font-medium text-green-900 mb-3 capitalize flex items-center">
            <Shield className="w-4 h-4 mr-2" />
            {recovery.exception_type?.replace('_', ' ')} Recovery
          </h5>

          {recovery.recovery_options?.map((option: any, optIndex: number) => (
            <div key={optIndex} className="mb-4 last:mb-0 bg-white rounded-lg p-3 border border-green-200">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-green-800">{option.option_name}</span>
                <div className="flex space-x-2">
                  <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {option.estimated_time}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${option.success_probability === 'high' ? 'bg-green-200 text-green-800' :
                    option.success_probability === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                    {option.success_probability} success rate
                  </span>
                </div>
              </div>

              <div className="mb-3">
                <span className="text-sm font-medium text-green-800">Procedure:</span>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-sm text-green-700">
                  {option.procedure?.map((step: string, stepIndex: number) => (
                    <li key={stepIndex}>{step}</li>
                  ))}
                </ol>
              </div>

              {option.resources_required && (
                <div className="text-sm">
                  <span className="font-medium text-green-800">Resources Required:</span>
                  <div className="text-green-700 mt-1">
                    {Array.isArray(option.resources_required)
                      ? option.resources_required.join(', ')
                      : option.resources_required
                    }
                  </div>
                </div>
              )}

              {option.business_impact_during_recovery && (
                <div className="text-sm mt-2">
                  <span className="font-medium text-green-800">Business Impact During Recovery:</span>
                  <div className="text-green-700 mt-1">{option.business_impact_during_recovery}</div>
                </div>
              )}
            </div>
          ))}

          {/* Prevention Measures */}
          {recovery.prevention_measures && (
            <div className="mt-3 p-3 bg-blue-50 rounded border border-blue-200">
              <span className="text-sm font-medium text-blue-800">Prevention Measures:</span>
              <ul className="list-disc list-inside mt-1 text-sm text-blue-700">
                {recovery.prevention_measures.map((measure: string, idx: number) => (
                  <li key={idx}>{measure}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )) || (
          <div className="text-center py-12 text-gray-500">
            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p>No recovery procedures defined for this step</p>
          </div>
        )}
    </div>
  );

  const renderEscalationTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Escalation Hierarchy</h4>
        <span className="text-sm text-gray-500">{Object.keys(escalationMatrix).length} escalation levels</span>
      </div>

      {Object.entries(escalationMatrix).map(([level, details]: [string, any], index: number) => (
        <div key={level} className="bg-white rounded-lg border border-blue-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h5 className="font-medium text-blue-900 capitalize flex items-center">
              <ArrowUp className="w-4 h-4 mr-2" />
              {level.replace('_', ' ')}
            </h5>
            <div className="flex items-center space-x-2">
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                <Clock className="w-3 h-3 inline mr-1" />
                {details.timeline}
              </span>
              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                Level {index + 1}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-blue-800">Role:</span>
              <div className="text-blue-700 mt-1">{details.role}</div>
            </div>
            <div>
              <span className="font-medium text-blue-800">Authority:</span>
              <div className="text-blue-700 mt-1">{details.authority}</div>
            </div>
          </div>

          {details.triggers && details.triggers.length > 0 && (
            <div className="mt-3 p-3 bg-yellow-50 rounded border border-yellow-200">
              <span className="text-sm font-medium text-yellow-800">Escalation Triggers:</span>
              <ul className="list-disc list-inside mt-1 text-sm text-yellow-700">
                {details.triggers.map((trigger: any, idx: number) => (
                  <li key={idx}>
                    {typeof trigger === 'string' ? trigger : (
                      <div>
                        <strong>{trigger.condition}</strong>
                        {trigger.action && <div className="ml-4">→ {trigger.action}</div>}
                        {trigger.timeline && <div className="ml-4 text-xs">Timeline: {trigger.timeline}</div>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {details.skills_required && (
            <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
              <span className="text-sm font-medium text-gray-800">Skills Required:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {details.skills_required.map((skill: string, idx: number) => (
                  <span key={idx} className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {details.notification_methods && (
            <div className="mt-3 text-sm">
              <span className="font-medium text-blue-800">Notification Methods:</span>
              <div className="text-blue-700 mt-1">
                {details.notification_methods.join(', ')}
              </div>
            </div>
          )}
        </div>
      ))}

      {Object.keys(escalationMatrix).length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No escalation hierarchy defined</p>
        </div>
      )}
    </div>
  );


  const renderControlsTab = () => {
    // Extract real data from the exception analysis
    const processType = processClassification?.process_type;
    const criticalityLevel = processClassification?.criticality_level;
    const businessImpact = processClassification?.business_impact;

    // Get controls data from step exception analysis
    const stepExceptionsList = stepData?.potential_exceptions || [];
    const hasSystemFailureRisk = stepExceptionsList.some((exc: { exception_type: string; }) => exc.exception_type === 'system_failure');
    const hasApprovalDelayRisk = stepExceptionsList.some((exc: { exception_type: string; }) => exc.exception_type === 'approval_delay');
    const hasDataValidationRisk = stepExceptionsList.some((exc: { exception_type: string; }) => exc.exception_type === 'data_validation');
    const hasHighImpactRisks = stepExceptionsList.some((exc: { business_impact: string; }) =>
      exc.business_impact === 'critical' || exc.business_impact === 'high'
    );

    // Get actual escalation data
    const escalationLevels = Object.keys(escalationMatrix || {});
    const hasEscalationControls = escalationLevels.length > 0;

    return (
      <div className="space-y-6">
        {/* Controls Bypassed - Dynamic based on step exception data */}
        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
          <h5 className="font-medium text-red-900 mb-3 flex items-center">
            <XCircle className="w-4 h-4 mr-2" />
            Controls Bypassed for This Step
          </h5>

          <div className="space-y-3">
            {/* Show bypassed controls based on actual exception types */}
            {hasApprovalDelayRisk && (
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <h6 className="font-medium text-red-800 mb-2">Standard Approval Process</h6>
                <p className="text-sm text-red-700 mb-2">
                  Normal approval workflow bypassed due to exception handling requirements.
                  Process type: {processType?.replace('_', ' ') || 'Standard Business Process'}
                </p>
                <div className="text-xs text-red-600">
                  <strong>Impact:</strong> Approval validation reduced for speed
                </div>
              </div>
            )}

            {hasSystemFailureRisk && (
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <h6 className="font-medium text-red-800 mb-2">System Validation Checks</h6>
                <p className="text-sm text-red-700 mb-2">
                  Automated system validation bypassed during exception processing.
                  Criticality level: {criticalityLevel?.replace('_', ' ') || 'Standard'}
                </p>
                <div className="text-xs text-red-600">
                  <strong>Impact:</strong> System reliability checks reduced
                </div>
              </div>
            )}

            {hasDataValidationRisk && (
              <div className="bg-white rounded-lg p-3 border border-red-200">
                <h6 className="font-medium text-red-800 mb-2">Data Quality Validation</h6>
                <p className="text-sm text-red-700 mb-2">
                  Standard data validation processes bypassed during exception handling.
                </p>
                <div className="text-xs text-red-600">
                  <strong>Impact:</strong> Data quality verification reduced
                </div>
              </div>
            )}

            {stepExceptionsList.length === 0 && (
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <p className="text-sm text-gray-600">
                  No control bypasses identified for this step based on exception analysis.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Compensating Controls - Dynamic based on actual data */}
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <h5 className="font-medium text-green-900 mb-3 flex items-center">
            <CheckCircle className="w-4 h-4 mr-2" />
            Compensating Controls for This Step
          </h5>

          <div className="space-y-3">
            {/* Show compensating controls based on actual exception analysis */}
            {hasEscalationControls && (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <h6 className="font-medium text-green-800 mb-2">Escalation Authority Structure</h6>
                <p className="text-sm text-green-700 mb-2">
                  {escalationLevels.length} level escalation hierarchy with defined approval authority.
                  Business impact level: {businessImpact?.operational || 'Standard'}
                </p>
                <div className="text-xs text-green-600">
                  <strong>Coverage:</strong> Management oversight and approval controls
                </div>
              </div>
            )}

            {recoveryPaths?.exception_recoveries?.length > 0 && (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <h6 className="font-medium text-green-800 mb-2">Recovery Procedures</h6>
                <p className="text-sm text-green-700 mb-2">
                  {recoveryPaths.exception_recoveries.length} recovery path(s) defined with
                  {recoveryPaths.exception_recoveries.reduce((sum: number, rec: any) =>
                    sum + (rec.recovery_options?.length || 0), 0)} total recovery options.
                </p>
                <div className="text-xs text-green-600">
                  <strong>Coverage:</strong> Exception recovery and remediation procedures
                </div>
              </div>
            )}

            {hasHighImpactRisks && (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <h6 className="font-medium text-green-800 mb-2">Enhanced Monitoring</h6>
                <p className="text-sm text-green-700 mb-2">
                  Increased monitoring controls due to {stepExceptionsList.filter((exc: { business_impact: string; }) =>
                    exc.business_impact === 'critical' || exc.business_impact === 'high'
                  ).length} high-impact risk(s) identified.
                </p>
                <div className="text-xs text-green-600">
                  <strong>Coverage:</strong> Real-time monitoring and alerting
                </div>
              </div>
            )}

            {processType === 'financial_transaction' && businessImpact?.financial === 'high' && (
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <h6 className="font-medium text-green-800 mb-2">Financial Controls</h6>
                <p className="text-sm text-green-700 mb-2">
                  Enhanced financial validation due to high financial impact classification.
                </p>
                <div className="text-xs text-green-600">
                  <strong>Coverage:</strong> Financial validation and audit trail
                </div>
              </div>
            )}

            {(!hasEscalationControls && !recoveryPaths?.exception_recoveries?.length && !hasHighImpactRisks) && (
              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                <p className="text-sm text-red-600 font-medium">
                  ⚠️ No exception analysis data available for this step.
                </p>
                <p className="text-xs text-red-500 mt-1">
                  Exception analysis may have failed during processing or step has no identified exceptions.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Control Assessment Summary - Dynamic metrics */}
        {stepExceptionsList.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h5 className="font-medium text-blue-900 mb-3 flex items-center">
              <Info className="w-4 h-4 mr-2" />
              Control Assessment Summary
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="text-center">
                <div className="font-bold text-blue-800">{stepExceptionsList.length}</div>
                <div className="text-blue-600">Exceptions Analyzed</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-blue-800">{escalationLevels.length}</div>
                <div className="text-blue-600">Escalation Levels</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-blue-800">
                  {stepExceptionsList.filter((exc: { likelihood: string; }) => exc.likelihood === 'high').length}
                </div>
                <div className="text-blue-600">High Risk Items</div>
              </div>
              <div className="text-center">
                <div className="font-bold text-blue-800">
                  {recoveryPaths?.exception_recoveries?.reduce((sum: number, rec: any) =>
                    sum + (rec.recovery_options?.length || 0), 0) || 0}
                </div>
                <div className="text-blue-600">Recovery Options</div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderExceptionProcessesTab = () => {
    // Get exception processes from the full analysis data
    const exceptionProcesses = fullAnalysis?.step_exceptions?.exception_processes?.filter(
      (proc: any) => proc.parent_step_name === stepName
    ) || [];

    if (exceptionProcesses.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <Workflow className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p>No dedicated exception processes defined for this step</p>
          <p className="text-sm mt-2">This step follows the standard process flow only</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-medium text-blue-900 mb-2">
            Exception Process Overview
          </h4>
          <p className="text-sm text-blue-700">
            This step has {exceptionProcesses.length} dedicated exception processes that handle specific failure scenarios.
            Each exception process bypasses certain standard controls and implements compensating measures.
          </p>
        </div>

        {exceptionProcesses.map((exceptionProcess: any, index: number) => (
          <div key={exceptionProcess.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Exception Process Header */}
            <div className="bg-orange-50 px-4 py-3 border-b border-orange-200">
              <div className="flex justify-between items-start">
                <div>
                  <h5 className="font-medium text-orange-900">
                    Exception Process {index + 1}: {exceptionProcess.process_name}
                  </h5>
                  <p className="text-sm text-orange-700 mt-1">
                    Trigger: {exceptionProcess.trigger_condition}
                  </p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${exceptionProcess.exception_type === 'system_failure' ? 'bg-red-100 text-red-800' :
                    exceptionProcess.exception_type === 'approval_delay' ? 'bg-yellow-100 text-yellow-800' :
                      exceptionProcess.exception_type === 'data_validation' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                  }`}>
                  {exceptionProcess.exception_type?.replace('_', ' ')}
                </span>
              </div>
            </div>

            <div className="p-4 space-y-4">
              {/* Exception Process Steps */}
              <div>
                <h6 className="font-medium text-gray-900 mb-2 flex items-center">
                  <List className="w-4 h-4 mr-2 text-blue-600" />
                  Exception Handling Steps
                </h6>
                <div className="bg-gray-50 rounded-lg p-3">
                  <ol className="space-y-2">
                    {(exceptionProcess.exception_steps || []).map((step: string, stepIndex: number) => (
                      <li key={stepIndex} className="flex items-start">
                        <span className="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs mr-3 mt-0.5 flex-shrink-0">
                          {stepIndex + 1}
                        </span>
                        <span className="text-sm text-gray-700">{step}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* Controls Analysis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                  <h6 className="font-medium text-red-800 mb-2 flex items-center">
                    <XCircle className="w-4 h-4 mr-2" />
                    Controls Bypassed
                  </h6>
                  <ul className="space-y-1">
                    {(exceptionProcess.controls_bypassed || []).map((control: string, idx: number) => (
                      <li key={idx} className="text-sm text-red-700 flex items-center">
                        <div className="w-2 h-2 bg-red-500 rounded-full mr-2 flex-shrink-0"></div>
                        {control}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <h6 className="font-medium text-green-800 mb-2 flex items-center">
                    <Shield className="w-4 h-4 mr-2" />
                    Compensating Controls
                  </h6>
                  <ul className="space-y-1">
                    {(exceptionProcess.compensating_controls || []).map((control: string, idx: number) => (
                      <li key={idx} className="text-sm text-green-700 flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2 flex-shrink-0"></div>
                        {control}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Approval and Monitoring */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                  <h6 className="font-medium text-blue-800 mb-2">Approval Authority</h6>
                  <p className="text-sm text-blue-700">{exceptionProcess.approval_authority}</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                  <h6 className="font-medium text-purple-800 mb-2">Monitoring</h6>
                  <ul className="space-y-1">
                    {(exceptionProcess.monitoring_requirements || []).slice(0, 2).map((req: string, idx: number) => (
                      <li key={idx} className="text-xs text-purple-700">• {req}</li>
                    ))}
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-200">
                  <h6 className="font-medium text-indigo-800 mb-2">Return Conditions</h6>
                  <ul className="space-y-1">
                    {(exceptionProcess.return_conditions || []).slice(0, 2).map((condition: string, idx: number) => (
                      <li key={idx} className="text-xs text-indigo-700">• {condition}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Process Flow Diagram */}
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <h5 className="font-medium text-gray-900 mb-3 flex items-center">
            <Workflow className="w-4 h-4 mr-2 text-purple-600" />
            Exception Process Flow
          </h5>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-4 flex-wrap">
              <div className="bg-green-100 border border-green-300 rounded-lg px-3 py-2 text-center">
                <div className="text-sm font-medium text-green-800">Standard Process</div>
                <div className="text-xs text-green-600">{stepName}</div>
              </div>
              <ArrowRight className="text-gray-400" />
              <div className="bg-red-100 border border-red-300 rounded-lg px-3 py-2 text-center">
                <div className="text-sm font-medium text-red-800">Exception Trigger</div>
                <div className="text-xs text-red-600">Condition Detected</div>
              </div>
              <ArrowRight className="text-gray-400" />
              <div className="bg-orange-100 border border-orange-300 rounded-lg px-3 py-2 text-center">
                <div className="text-sm font-medium text-orange-800">Exception Process</div>
                <div className="text-xs text-orange-600">{exceptionProcesses.length} alternative paths</div>
              </div>
              <ArrowRight className="text-gray-400" />
              <div className="bg-blue-100 border border-blue-300 rounded-lg px-3 py-2 text-center">
                <div className="text-sm font-medium text-blue-800">Return/Escalate</div>
                <div className="text-xs text-blue-600">Resume or Escalate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Process Flow', icon: BarChart3 },
    { id: 'exception_processes', label: 'Exception Processes', icon: Workflow }, // NEW
    { id: 'exceptions', label: 'Risk Analysis', icon: AlertTriangle },
    { id: 'recovery', label: 'Recovery Paths', icon: RotateCcw },
    { id: 'escalation', label: 'Escalation Matrix', icon: Users },
    { id: 'controls', label: 'Control Analysis', icon: Shield }
  ];

  return (
    <>
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #F3F4F6;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #93C5FD;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #60A5FA;
        }
      `}</style>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-2 text-orange-600" />
                Exception Logic & Decision Tree
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Step: {stepName}
              </p>
              <div className="text-xs text-gray-500 mt-1">
                {potentialExceptions.length} exceptions identified • {statistics.analysis_quality || 'Standard'} quality analysis
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-light"
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8 overflow-x-auto mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'exception_processes' && renderExceptionProcessesTab()} 
          {activeTab === 'exceptions' && renderExceptionsTab()}
          {activeTab === 'recovery' && renderRecoveryTab()}
          {activeTab === 'escalation' && renderEscalationTab()}
          {activeTab === 'controls' && renderControlsTab()}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              <span>Step ID: </span>
              <code className="bg-gray-200 px-1 rounded">{stepId}</code>
              <span className="ml-4">Process Map: </span>
              <code className="bg-blue-100 px-1 rounded text-blue-800">{processMapId}</code>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default ExceptionAnalysisPopup;